// packages/shared/src/billing/types.ts
//
// Phase 3.3 cross-module billing primitives — order charge, billing mode,
// auto-pay, auto-recharge, suspend state, and BullMQ job payload contracts.
//
// Decision refs:
//   - Auto Payment (modes, atomicity, retry FIFO)
//   - Auto-Recharge (threshold/amount, retry schedule, max-per-day, disable on fail)
//   - Order Charge Contract (outbox pattern, pending_payment state, FulfillmentSnapshot)
//   - Merchant Suspend Flow (view-only, banner, mutation block)
//   - Merchant Onboarding Billing (defaults, wallet auto-create)
//
// Node-pure (no Fastify / Mongoose). Safe to bundle in Vite client builds.
// Runtime deps: zod only (per Wave 1 acceptance criterion).
//
// Wave-1 alignment (per wave_context.md):
//   - billing_mode: snake_case in Mongo doc, BillingMode union in TS
//   - auto_pay_settings: snake_case nested in Mongo
//   - payment_state: snake_case enum on Order doc (draft|pending_payment|paid|cancelled)
// These TS-side enums export both the SCREAMING_SNAKE union (BillingMode, etc.)
// and a `parseXxx` Zod helper so service-layer mappers can safely round-trip
// snake_case Mongo documents into the typed shape used by route handlers + FE.
import { z } from "zod";
// ---------------------------------------------------------------------------
// Inline ObjectIdHex schema — mirrored from packages/shared/src/wallet/types.ts
// per Phase 3.1 convention (avoid hard ordering dependency during parallel
// Wave 1 builds; reviewer dedupes post-wave).
// TODO(post-Wave-1): import ObjectIdHexSchema from '../ledger/types.js'
// ---------------------------------------------------------------------------
/** MongoDB ObjectId hex string (24 lowercase hex chars). */
const ObjectIdHexSchema = z.string().regex(/^[a-f0-9]{24}$/, {
    message: "Must be a 24-character lowercase hex ObjectId",
});
// ===========================================================================
// BillingMode — merchant payment model (future P3.5 invoice scope)
// ===========================================================================
//
// PREPAID  — wallet-funded, charged at order placement
// CREDIT   — credit facility (Phase 3.4b), charged on settlement
// INVOICE  — periodic invoice billing (Phase 3.5 scope). Phase 3.3 PARSES the
//            value but the service layer rejects it with HTTP 409 because
//            invoice mode is not yet wired. JSDoc on the service handler MUST
//            document this rejection explicitly.
const BILLING_MODES = ["PREPAID", "CREDIT", "INVOICE"];
export const BillingModeSchema = z.enum(BILLING_MODES);
/** Const-asserted union list for runtime iteration (e.g. settings dropdown). */
export const BillingModes = BILLING_MODES;
/** Zod safe-parse helper for BillingMode. Throws on invalid input. */
export function parseBillingMode(input) {
    return BillingModeSchema.parse(input);
}
// ===========================================================================
// AutoPayMode — auto-pay funding source preference
// ===========================================================================
//
// WALLET_ONLY         — only debit wallet; fall through to unpaid if short
// WALLET_PLUS_CREDIT  — try wallet first; if wallet covers FULL amount → debit
//                       wallet; else if credit covers FULL amount → debit credit
//                       (no split: 1 source must cover entire order)
const AUTO_PAY_MODES = ["WALLET_ONLY", "WALLET_PLUS_CREDIT"];
export const AutoPayModeSchema = z.enum(AUTO_PAY_MODES);
/** Const-asserted list for UI iteration. */
export const AutoPayModes = AUTO_PAY_MODES;
/** Zod parse helper for AutoPayMode. Throws on invalid input. */
export function parseAutoPayMode(input) {
    return AutoPayModeSchema.parse(input);
}
// ===========================================================================
// OrderPaymentState — order-level payment state slice
// ===========================================================================
//
// Mirrors the snake_case enum stored on Order documents (see Wave 1 Task 3).
// Matches the P4 state machine subset that Phase 3.3 cares about; broader
// fulfillment states (`processing`, etc.) live in Phase 4 model definitions.
const ORDER_PAYMENT_STATES = [
    "draft",
    "pending_payment",
    "paid",
    "cancelled",
];
export const OrderPaymentStateSchema = z.enum(ORDER_PAYMENT_STATES);
/** Const-asserted list for runtime iteration. */
export const OrderPaymentStates = ORDER_PAYMENT_STATES;
/** Zod parse helper for OrderPaymentState. */
export function parseOrderPaymentState(input) {
    return OrderPaymentStateSchema.parse(input);
}
// ===========================================================================
// OrderCharge — wallet/credit charge contract for one order
// ===========================================================================
//
// One order = one ORDER_CHARGE TxGroup. `attempts` increments on
// each auto-pay retry. Amount stored as integer cents (wallet invariant).
export const OrderChargeSchema = z.object({
    /** Order ObjectId being charged. */
    orderId: ObjectIdHexSchema,
    /** Owning merchant ObjectId (parent — members do not have own settings). */
    merchantId: ObjectIdHexSchema,
    /**
     * Charge amount in integer cents (>= 1, never zero or negative).
     * Source: order total at `pending_payment` snapshot time.
     */
    amountCents: z.number().int().min(1, {
        message: "amountCents must be >= 1 (positive integer cents)",
    }),
    /** Auto-pay mode applied at the time of THIS charge attempt. */
    mode: AutoPayModeSchema,
    /**
     * Number of auto-pay attempts made so far for this order, including the
     * current one. First attempt = 1. Auto-pay retry has no per-order
     * cap; field exists for telemetry / FIFO ordering tie-break only.
     */
    attempts: z.number().int().min(1),
    /** Order payment state at the time the charge was attempted. */
    state: OrderPaymentStateSchema,
    /** Idempotency key (BullMQ job id or outbox row id) — at-least-once safety. */
    idempotencyKey: z.string().min(1).max(128),
});
/** Zod parse helper for OrderCharge. */
export function parseOrderCharge(input) {
    return OrderChargeSchema.parse(input);
}
// ===========================================================================
// SuspendState — merchant-level suspend flag
// ===========================================================================
//
// View-only mode (Amendment #3): merchant + members CAN log in, mutations are
// blocked at the API middleware with HTTP 423 BILLING_MERCHANT_SUSPENDED.
// Reactivation is admin-driven; no JWT revoke, no logout.
export const SuspendStateSchema = z.object({
    /** True when merchant is currently suspended. */
    isSuspended: z.boolean(),
    /**
     * Admin-supplied reason (mandatory at suspend time).
     * Empty string allowed only when isSuspended === false.
     */
    reason: z.string().max(500),
    /**
     * Suspend timestamp — unix milliseconds; null when not suspended.
     * Cleared on reactivate.
     */
    suspendedAt: z.number().int().nonnegative().nullable(),
    /**
     * Admin user ObjectId who triggered the suspend; null when not suspended.
     * Cleared on reactivate; audit log keeps full history.
     */
    suspendedBy: ObjectIdHexSchema.nullable(),
});
/** Zod parse helper for SuspendState. */
export function parseSuspendState(input) {
    return SuspendStateSchema.parse(input);
}
// ===========================================================================
// AutoRechargeSettings — auto-recharge config + runtime counters
// ===========================================================================
//
// Persisted on the merchant doc (snake_case nested per Wave 1 alignment).
// Fail-counter resets on a successful recharge; reaching 3 disables the
// feature (AUTORECHARGE_FAIL_COUNT_THRESHOLD hardcoded const, NOT env, per
// Wave 1 Task 2 alignment).
export const AutoRechargeSettingsSchema = z.object({
    /** Master toggle — only true when paymentMethodId is also set. */
    enabled: z.boolean(),
    /**
     * Trigger threshold in integer cents. Auto-recharge fires when wallet
     * balance CROSSES this value from above. Same value drives the
     * low-balance alert.
     */
    thresholdCents: z.number().int().min(0),
    /**
     * Recharge amount in integer cents. Must be >= configured min top-up
     * (validated server-side using gateway-min from env / config — Phase 3.2).
     */
    amountCents: z.number().int().min(1),
    /**
     * Linked payment method id (server-to-server capable gateway only).
     * Null when not yet configured; required when `enabled === true`.
     */
    paymentMethodId: z.string().min(1).nullable(),
    /**
     * Max recharge attempts per UTC day (admin-config).
     * Exceeding → auto-disable + notify merchant.
     */
    maxPerDay: z.number().int().min(1),
    /**
     * Consecutive failure counter. Reset on successful recharge.
     * Reaching AUTORECHARGE_FAIL_COUNT_THRESHOLD (3, hardcoded per Wave 1 Task 2)
     * disables auto-recharge automatically.
     */
    failCount: z.number().int().min(0),
    /**
     * Timestamp (unix ms) when auto-recharge was auto-disabled by the system.
     * Null when never disabled or after merchant manually re-enables.
     */
    disabledAt: z.number().int().nonnegative().nullable(),
});
/** Zod parse helper for AutoRechargeSettings. */
export function parseAutoRechargeSettings(input) {
    return AutoRechargeSettingsSchema.parse(input);
}
// ===========================================================================
// LowBalanceThresholds — alert + auto-recharge shared thresholds
// ===========================================================================
//
// Phase 3.3 uses ONE shared threshold per merchant for both low-balance alert
// and auto-recharge trigger. The `tiers` array stores
// the descending integer-cent threshold ladder parsed from the env var
// `BILLING_LOW_BALANCE_THRESHOLDS_USD` (CSV → DESC int cents — Wave 1 Task 2).
//
// `currentTierIndex` is the highest tier currently above wallet balance (i.e.
// the tier that fired the most-recent alert). -1 means no tier crossed.
// Re-arming logic: when balance climbs back above a tier and crosses it
// downward again, the alert re-fires.
export const LowBalanceThresholdsSchema = z.object({
    /** Tier ladder in descending integer cents (e.g. [10000, 5000, 1000]). */
    tiers: z
        .array(z.number().int().nonnegative())
        .refine((arr) => arr.every((v, i, a) => i === 0 || a[i - 1] === undefined || v <= a[i - 1]), { message: "tiers must be sorted descending (DESC int cents)" }),
    /**
     * Index into `tiers` for the most-recently-fired alert tier; -1 if none.
     * Allows the alert worker to detect re-arm on balance climb + recross.
     */
    currentTierIndex: z.number().int().min(-1),
    /** Last time an alert was emitted (unix ms); null when never fired. */
    lastFiredAt: z.number().int().nonnegative().nullable(),
});
/** Zod parse helper for LowBalanceThresholds. */
export function parseLowBalanceThresholds(input) {
    return LowBalanceThresholdsSchema.parse(input);
}
// ===========================================================================
// AutoPayRetryJobPayload — BullMQ job for auto-pay FIFO retry
// ===========================================================================
//
// Enqueued with delay 5–10s (debounce, gomb multiple inflows into 1 retry pass)
// when the wallet receives any inflow (topup approve, IMAP match, refund,
// cashback, transfer received, manual recharge). Worker iterates merchant's
// `pending_payment` orders FIFO by createdAt, calls the same charge flow
// (does NOT bypass state machine or outbox), skips orders whose state has
// since flipped to `cancelled`. Retry is unbounded per order.
export const AutoPayRetryJobPayloadSchema = z.object({
    /** Merchant whose unpaid orders should be retried (parent only). */
    merchantId: ObjectIdHexSchema,
    /**
     * Why the retry was scheduled (telemetry only — affects logging, not logic).
     * Worker behaviour reads from current merchant settings (: retry
     * always checks setting at retry time, not at enqueue time).
     */
    trigger: z.enum([
        "topup_approved",
        "imap_match",
        "refund",
        "cashback",
        "transfer_received",
        "auto_recharge_success",
        "manual_settings_save",
    ]),
    /** Enqueue timestamp (unix ms). Used for debounce coalescing. */
    enqueuedAt: z.number().int().nonnegative(),
    /**
     * Idempotency key — `{merchantId}:{trigger}:{minute-bucket}` is the
     * canonical form so duplicate inflows in the same minute coalesce.
     */
    idempotencyKey: z.string().min(1).max(128),
});
/** Zod parse helper for AutoPayRetryJobPayload. */
export function parseAutoPayRetryJobPayload(input) {
    return AutoPayRetryJobPayloadSchema.parse(input);
}
// ===========================================================================
// AutoRechargeJobPayload — BullMQ job for auto-recharge attempt
// ===========================================================================
//
// Enqueued when wallet balance crosses below the configured threshold and
// auto-recharge is enabled. Retry schedule on failure is fixed: 1h → 6h →
// 24h. After 3 consecutive failures the worker disables
// auto-recharge and notifies the merchant.
const AUTO_RECHARGE_RETRY_DELAYS_HOURS = [1, 6, 24];
/** Fixed retry delay schedule (hours) for failed recharge attempts. */
export const AutoRechargeRetryDelaysHours = AUTO_RECHARGE_RETRY_DELAYS_HOURS;
export const AutoRechargeJobPayloadSchema = z.object({
    /** Merchant whose wallet triggered the recharge. */
    merchantId: ObjectIdHexSchema,
    /**
     * Wallet balance (cents) at the moment threshold was crossed. Snapshot
     * for telemetry — worker re-reads live balance before charging.
     */
    triggeredAtBalanceCents: z.number().int().nonnegative(),
    /** Threshold (cents) that was crossed. */
    thresholdCents: z.number().int().min(0),
    /** Configured recharge amount (cents). */
    amountCents: z.number().int().min(1),
    /** Linked payment method id used for this attempt. */
    paymentMethodId: z.string().min(1),
    /**
     * 1-indexed attempt number for this recharge cycle.
     * 1 = first try (immediate). 2..N follow `AutoRechargeRetryDelaysHours`.
     * Reset to 1 on a successful recharge.
     */
    attempt: z.number().int().min(1),
    /**
     * Idempotency key — `{merchantId}:{thresholdCents}:{cycleId}` ensures a
     * single recharge attempt per crossing event (does not coalesce retries).
     */
    idempotencyKey: z.string().min(1).max(128),
});
/** Zod parse helper for AutoRechargeJobPayload. */
export function parseAutoRechargeJobPayload(input) {
    return AutoRechargeJobPayloadSchema.parse(input);
}
//# sourceMappingURL=types.js.map